import { Button, Space, TableProps, Dropdown, Menu, Modal, Input, Select, DatePicker, Badge, Tag } from "antd";
import {
  IApplicant,
  IJob,
  IOnDelete,
  IOnProjectSelect,
  IProjectDetails,
  IScheduleCall,
} from "./interfaces";
import { EditablePriority } from "../projectsPanel";
import { MessageOutlined } from "@ant-design/icons";
import EmailRequestBtn from "../applicantsPanel/emailRequest";
import { StatusType, formatInterviewDateTime } from "../../common";
import { ChatBadge } from "../../common/ChatBadge";
import { Link } from "react-router-dom";

// Helper function to format status for display
const formatStatus = (status: string): { label: string; color: string } => {
  const statusMap: { [key: string]: { label: string; color: string } } = {
    'pending': { label: 'Pending', color: 'default' },
    'submitted': { label: 'Submitted', color: 'blue' },
    'under_review': { label: 'Under Review', color: 'processing' },
    'shortlisted': { label: 'Shortlisted', color: 'cyan' },
    'shortlisted_manager': { label: 'Shortlisted by Manager', color: 'cyan' },
    'shortlisted_by_manager': { label: 'Shortlisted by Manager', color: 'cyan' },
    'shortlisted_recruiter': { label: 'Shortlisted by Recruiter', color: 'cyan' },
    'shortlisted_by_recruiter': { label: 'Shortlisted by Recruiter', color: 'cyan' },
    'interviewing': { label: 'Interviewing', color: 'purple' },
    'interviewing_manager': { label: 'Interview with Manager', color: 'purple' },
    'interview_scheduled': { label: 'Interview Scheduled', color: 'geekblue' },
    'offer_sent': { label: 'Offer Sent', color: 'orange' },
    'offer_placed': { label: 'Offer Placed', color: 'orange' },
    'offer_accepted': { label: 'Offer Accepted', color: 'success' },
    'offer_rejected': { label: 'Offer Rejected', color: 'error' },
    'offer_revoked': { label: 'Offer Revoked', color: 'default' },
    'offer_expired': { label: 'Offer Expired', color: 'default' },
    'hired': { label: 'Hired', color: 'success' },
    'rejected': { label: 'Rejected', color: 'error' },
    'rejected_manager': { label: 'Rejected by Manager', color: 'error' },
    'rejected_by_manager': { label: 'Rejected by Manager', color: 'error' },
    'rejected_recruiter': { label: 'Rejected by Recruiter', color: 'error' },
    'rejected_by_recruiter': { label: 'Rejected by Recruiter', color: 'error' },
    'approved_manager': { label: 'Approved by Manager', color: 'success' },
    'approved_by_manager': { label: 'Approved by Manager', color: 'success' },
    'withdrawn': { label: 'Withdrawn', color: 'default' },
    'on_hold': { label: 'On Hold', color: 'warning' },
  };
  
  if (statusMap[status]) {
    return statusMap[status];
  }
  
  // Fallback: convert snake_case to Title Case
  const label = status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  
  return { label, color: 'default' };
};

export const toHHMMSS = (val = 0) => {
  const hours = Math.floor(val / 3600);
  const minutes = Math.floor((val % 3600) / 60);
  let output = "";
  if (hours) output += `${hours} hr `;
  if (minutes) output += `${minutes} mins `;
  return output;
};

export function renderFeedback(text: string | undefined) {
  if (!text) return <span>—</span>;
  const lines = text.split(/\n/);
  const elements: JSX.Element[] = [];
  let bullets: string[] = [];
  lines.forEach((line: string, idx: number) => {
    const trimmed = line.trim();
    if (/^[-•*]\s+/.test(trimmed)) {
      bullets.push(trimmed.replace(/^[-•*]\s+/, ""));
    } else {
      if (bullets.length) {
        elements.push(
          <ul key={`ul-fb-${idx}`}>
            {bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        );
        bullets = [];
      }
      if (trimmed) {
        elements.push(<p key={`p-fb-${idx}`}>{trimmed}</p>);
      }
    }
  });
  if (bullets.length) {
    elements.push(
      <ul key={`ul-fb-last`}>
        {bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    );
  }
  return elements;
}

export const ApplicantsColumns = (
  onDelete: IOnDelete,
  scheduleCall: IScheduleCall,
  sendEmailRequest: (id: string, emailBody: string, subject: string) => Promise<void>,
  openRemarks: IOnDelete,
  project: IProjectDetails | undefined,
  openMoreInfo: (applicant: IApplicant) => void,
  openTalentPool: (applicant: IApplicant) => void,
  refetch?: () => void,
  setOfferModal?: (modal: { visible: boolean; applicationId?: string }) => void,
  markAsHired?: (applicationId: string) => void,
  openOfferModal?: (record: IApplicant) => void,
  setViewOfferModal?: (modal: { visible: boolean; record?: IApplicant }) => void,
  openRecruiterReject?: (applicationId: string) => void,
  readFeedbackIds?: Set<string>,
  openSendRejection?: (record: IApplicant) => void,
  // Unread recruiter-manager messages per jobApplicationId. The panel already
  // fetched these; nothing ever rendered them, so this chat had no badge while
  // the Jobs tab did.
  remarksUnreadCounts?: { [jobApplicationId: string]: number },
): TableProps<IApplicant>["columns"] => {
  const menu = (record: IApplicant) => {
    // Debug: Let's see what values we have
    console.log('Menu Debug - Applicant:', {
      name: record.name,
      status: record.status,
      offerAccepted: record.offerAccepted,
      offerRejected: record.offerRejected
    });

    // Place Offer should be disabled if: offer already placed, accepted, or hired
    const shouldDisablePlaceOffer = record.status === 'offer_placed' || 
                                   record.status === 'offer_accepted' || 
                                   record.status === 'hired';

    // Mark as Hired should be enabled when: offer is accepted and not already hired
    const shouldDisableMarkHired = record.status === 'hired' || 
                                  (record.status !== 'offer_accepted' && !record.offerAccepted);

    console.log('Menu Logic:', {
      shouldDisablePlaceOffer,
      shouldDisableMarkHired
    });

    // Reject: only block if already formally rejected by recruiter, hired, or withdrawn
    const shouldDisableReject = ['rejected_recruiter', 'hired', 'withdrawn'].includes(record.status || '');

    return (
      <Menu>
        <Menu.Item
          key="placeOffer"
          disabled={shouldDisablePlaceOffer}
          onClick={() => openOfferModal?.(record)}
        >
          Place Offer
        </Menu.Item>
        <Menu.Item
          key="markHired"
          disabled={shouldDisableMarkHired}
          onClick={() => markAsHired && markAsHired(record._id)}
        >
          Mark as Hired
        </Menu.Item>
        <Menu.Item
          key="reject"
          disabled={shouldDisableReject}
          danger
          onClick={() => openRecruiterReject?.(record._id)}
        >
          Reject Candidate
        </Menu.Item>
      </Menu>
    );
  };

  return [
    {
      title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Name</span>,
      dataIndex: "name",
      key: "name",
      align: "center",
      render: (text: string) => <span style={{ fontSize: '18px', fontWeight: 500 }}>{text}</span>,
    },
    {
      title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Schedule Interview</span>,
      key: "scheduleInterview",
      align: "center",
      render: (_: any, record: IApplicant) => {
        if (record.secondInterviewDate) {
          // Same "nowhere left to reschedule to" set used on the 1st interview.
          const blocked = ['rejected_recruiter', 'rejected_by_recruiter', 'rejected_manager', 'rejected_by_manager', 'hired', 'withdrawn', 'offer_rejected'].includes(record.status || '');
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <span style={{ color: "green", fontWeight: 600, fontSize: '18px' }}>Scheduled</span>
              {!blocked && (
                <Button size="small" onClick={() => scheduleCall(record, true)} style={{ fontSize: '13px' }}>
                  Reschedule
                </Button>
              )}
            </div>
          );
        }
        if (record.status === "shortlisted_manager") {
          return (
            <Button type="primary" onClick={() => scheduleCall(record)} style={{ fontSize: '16px', height: '40px' }}>
              Schedule
            </Button>
          );
        }
        if (record.status === "submitted") {
          return <span style={{ color: "#faad14", fontWeight: 600, fontSize: '18px' }}>Waiting for approval</span>;
        }
        if (record.status === "rejected_manager") {
          return (
            <Button danger onClick={() => openSendRejection?.(record)} style={{ fontSize: '16px', height: '40px' }}>
              Send Rejection
            </Button>
          );
        }
        return null;
      }
    },
    {
      title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Meeting Type</span>,
      dataIndex: "secondInterviewType",
      key: "secondInterviewType",
      align: "center",
      render: (_, { secondInterviewType }) => {
        const typeStr = String(secondInterviewType || "");
        return <span style={{ fontSize: '18px' }}>{typeStr && typeStr.trim() && typeStr !== "N/A"
          ? typeStr.split('(')[0]
          : "N/A"}</span>;
      },
    },
    {
      title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Interview Date</span>,
      dataIndex: "secondInterviewDate",
      key: "secondInterviewDate",
      align: "center",
      render: (_, { secondInterviewDate, secondInterviewLink }) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '18px' }}>
            {secondInterviewDate && secondInterviewDate !== "N/A"
              ? formatInterviewDateTime(secondInterviewDate)
              : secondInterviewDate || "N/A"}
          </span>
          {secondInterviewLink && (
            <a
              href={secondInterviewLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '14px', fontWeight: 600 }}
            >
              Join interview
            </a>
          )}
        </div>
      ),
    },
    {
      title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Status</span>,
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => {
        const { label, color } = formatStatus(status || '');
        return (
          <Tag 
            color={color} 
            style={{ 
              fontSize: '16px', 
              padding: '6px 14px',
              borderRadius: '4px',
            }}
          >
            {label}
          </Tag>
        );
      }
    },
    {
      title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Reminder Email</span>,
      dataIndex: "isEmailSent",
      key: "isEmailSent",
      align: "center",
      render: (_, data) => {
        if (data.status?.toLowerCase() !== StatusType.INTERVIEWING_MANAGER) return null;
        return (
          <EmailRequestBtn
            data={data}
            sendEmail={sendEmailRequest}
          />
        );
      },
    },
    {
      title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Remarks</span>,
      dataIndex: "remarks",
      align: "center",
      render: (_, { jobApplicationId, remarks }) =>
        remarks ? (
          <ChatBadge count={remarksUnreadCounts?.[jobApplicationId || ""] || 0}>
            <MessageOutlined
              style={{ fontSize: 32, color: "#002140", cursor: "pointer" }}
              onClick={() => {
                console.log("JobApplicationId sent to remarks:", jobApplicationId);
                openRemarks(jobApplicationId || "");
              }}
            />
          </ChatBadge>
        ) : (
          ""
        ),
    },
    {
      title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Recruiter Notes</span>,
      key: 'managerFeedback',
      align: 'center',
      width: 170,
      render: (_: any, record: any) => {
        const hasFeedback = !!(record.managerFeedback || record.feedback);
        const isUnread = hasFeedback && !readFeedbackIds?.has(record._id);
        return (
          <Badge
            count={isUnread ? '!' : 0}
            style={{ backgroundColor: '#ff4d4f', fontWeight: 700, fontSize: 13, minWidth: 20, height: 20, lineHeight: '20px' }}
            offset={[-6, 6]}
          >
            <Button onClick={() => openTalentPool(record)} style={{ fontSize: '16px', height: '40px' }}>
              Notes & Feedback
            </Button>
          </Badge>
        );
      },
    },
    {
      title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Profile</span>,
      key: 'profile',
      align: 'center',
      render: (_: any, record: any) => (
        <Button onClick={() => openMoreInfo(record)} style={{ fontSize: '16px', height: '40px' }}>View Profile</Button>
      ),
    },
    {
      title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Offer</span>,
      key: 'offer',
      render: (_: any, record: IApplicant) => {
        if (!record.offerLetter) return <span style={{ fontSize: '18px' }}>—</span>;
        let buttonType: "default" | "link" | "text" | "primary" | "dashed" | undefined = "default";
        let buttonStyle: React.CSSProperties = { fontSize: '16px', height: '40px' };
        if (record.offerAccepted) {
          buttonType = "primary";
          buttonStyle = { ...buttonStyle, backgroundColor: '#52c41a', borderColor: '#52c41a' };
        } else if (record.offerRejected) {
          buttonType = "primary";
          buttonStyle = { ...buttonStyle, backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' };
        }
        return (
          <Button
            type={buttonType}
            style={buttonStyle}
            onClick={() => setViewOfferModal && setViewOfferModal({ visible: true, record })}
          >
            View Offer
          </Button>
        );
      }
    },
    {
      title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Final Placement</span>,
      key: "finalPlacement",
      align: "center",
      render: (_: any, record: IApplicant) => (
        <Dropdown overlay={menu(record)} trigger={['click']}>
          <Button style={{ fontSize: '16px', height: '40px' }}>Actions</Button>
        </Dropdown>
      )
    },
  ];
};

export const ProjectsColumns = (
  onSelect: IOnProjectSelect
): TableProps<IProjectDetails>["columns"] => [
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Name</span>,
    dataIndex: "name",
    key: "name",
    align: "center" as const,
    render: (_, { name, _id }) => (
      <b
        onClick={() => onSelect(_id)}
        style={{ cursor: "pointer", color: "#00e", fontSize: '18px' }}
      >
        {name}
      </b>
    ),
  },
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Priority</span>,
    dataIndex: "priority",
    key: "priority",
    width: 200,
    align: "center" as const,
    render: (_, { _id, priority }) => (
      <EditablePriority id={_id} initialValue={priority} />
    ),
  },
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Total Applicants</span>,
    dataIndex: "applicantsCount",
    key: "applicantsCount",
    align: "center" as const,
    render: (_, { applicantsCount }) => <span style={{ fontSize: '18px' }}>{applicantsCount || 0}</span>,
  },
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Completion</span>,
    dataIndex: "completionPercentage",
    key: "completionPercentage",
    align: "center" as const,
    render: (_, { completionPercentage }) => <span style={{ fontSize: '18px' }}>{`${completionPercentage}%`}</span>,
  },
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Hiring Managers</span>,
    dataIndex: "managers",
    key: "managers",
    align: "center" as const,
    render: (_, { managers }) => (
      <Space size="middle" style={{ fontSize: '18px' }}>{managers?.map((e) => e.name)}</Space>
    ),
  },
];

export const JobsColumns = (
  onInvite: (job: IJob) => void,
  onMoreInfo: (job: IJob) => void,
  onViewInvites: (job: IJob) => void,
  hasInviteUpdatesForJob: (jobId: string) => boolean,
  hasUnviewedApplicationsForJob: (jobId: string) => boolean,
  jobChatCounts?: { [jobId: string]: number },
  onConfigureScreening?: (job: IJob) => void
): TableProps<IJob>["columns"] => [
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Company</span>,
    dataIndex: "companyName",
    key: "companyName",
    align: "center" as const,
    render: (text, record) => {
      const chatCount = jobChatCounts?.[record._id] || 0;
      return (
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {hasUnviewedApplicationsForJob(record._id) && (
            <Badge dot style={{ marginRight: 6 }} />
          )}
          <ChatBadge count={chatCount} offset={[10, -5]}>
            <Link to={`/recruiter/jobs/${record._id}/applications`} style={{ fontSize: '18px', fontWeight: '500' }}>
              {text}
            </Link>
          </ChatBadge>
        </span>
      );
    },
  },
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Position</span>,
    dataIndex: "positionTitle",
    key: "positionTitle",
    align: "center" as const,
    render: (text: string) => <span style={{ fontSize: '18px' }}>{text}</span>,
  },
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Work Type</span>,
    dataIndex: "workType",
    key: "workType",
    align: "center" as const,
    render: (text: string) => <span style={{ fontSize: '18px' }}>{text}</span>,
  },
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Location</span>,
    dataIndex: "location",
    key: "location",
    align: "center" as const,
    render: (text: string) => <span style={{ fontSize: '18px' }}>{text}</span>,
  },
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>View Job</span>,
    key: "moreInfoJob",
    align: "center" as const,
    render: (_, record) => (
      <Button onClick={() => onMoreInfo(record)} style={{ fontSize: '16px', height: '40px' }}>
        Job Details
      </Button>
    ),
  },
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Invite</span>,
    key: "invite",
    align: "center" as const,
    render: (_, record) => (
      <Button type="primary" onClick={() => onInvite(record)} style={{ fontSize: '16px', height: '40px' }}>
        Invite Candidate
      </Button>
    ),
  },
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Invites</span>,
    key: "invites",
    align: "center" as const,
    render: (_, record) => (
      <Badge dot={hasInviteUpdatesForJob(record._id)}>
        <Button onClick={() => onViewInvites(record)} style={{ fontSize: '16px', height: '40px' }}>
          View Invites
        </Button>
      </Badge>
    ),
  },
  {
    title: <span style={{ fontSize: '20px', fontWeight: 600 }}>Screening</span>,
    key: "screening",
    align: "center" as const,
    render: (_, record) => (
      <Button 
        type="default" 
        onClick={() => onConfigureScreening?.(record)}
        style={{ backgroundColor: '#f0f8ff', borderColor: '#1890ff', color: '#1890ff', fontSize: '16px', height: '40px' }}
      >
        🎯 Configure
      </Button>
    ),
  },
];

export const PREDEFINED_SCREENING_QUESTIONS = [
  "What is your notice period?",
  "Are you willing to relocate?",
  "What are your salary expectations?",
  "Are you authorized to work in the U.S.A?",
  "Would you now or in the future require visa sponsorship for this position?",
  // ...add more as needed
];

export const recruiterVisibleStatuses = [
  "submitted",
  "pending_manager",
  "shortlisted_manager",
  "interviewing_manager",
  "approved_manager",
  "rejected_manager",
  "hired",
  "offer_placed",
  "offer_accepted",
  "offer_rejected",
  "offer_revoked",
  "offer_expired",
];

